// components/JourneyTableOfContents.js

// frontend/app/components/JourneyTableOfContents.js

import React, { useMemo } from "react";
import {
  ScrollView,
  Text,
  TouchableOpacity,
  View,
} from "react-native";

import { getAuthoredJourneyStepTitle } from "./journeys/journeyContentRegistry";

export default function JourneyTableOfContents({
  journey,
  activeStepIndex = 0,
  visitedThroughIndex = 0,
  restrictToVisited = false,
  onStepChange,
  isDarkMode = false,
  phaseLabels = null,
  lang,
  t = (value) => value,
}) {
  const steps = useMemo(() => {
    if (Array.isArray(journey?.steps)) {
      return journey.steps;
    }

    if (Array.isArray(journey?.runtime?.steps)) {
      return journey.runtime.steps;
    }

    return [];
  }, [journey]);

  const journeyTitle =
    journey?.title ||
    journey?.name ||
    journey?.manifest?.title ||
    t("Learning Journey");

  const colors = {
    background: isDarkMode ? "#111827" : "#ffffff",
    headerBackground: isDarkMode ? "#0f172a" : "#f8fafc",
    text: isDarkMode ? "#f8fafc" : "#0f172a",
    muted: isDarkMode ? "#94a3b8" : "#64748b",
    border: isDarkMode
      ? "rgba(148, 163, 184, 0.24)"
      : "#dbe4ee",
    accent: isDarkMode ? "#38bdf8" : "#0369a1",
    accentSoft: isDarkMode
      ? "rgba(56, 189, 248, 0.12)"
      : "#e0f2fe",
    visited: isDarkMode ? "#4ade80" : "#15803d",
    visitedSoft: isDarkMode
      ? "rgba(74, 222, 128, 0.10)"
      : "#f0fdf4",
  };

  const journeySlug = String(
    journey?.slug || journey?.runtime?.slug || ""
  );

  const getStepTitle = (step, index) =>
    getAuthoredJourneyStepTitle({
      journeySlug,
      stepId: step?.step_id || step?.id,
      lang,
    }) ||
    step?.title ||
    step?.name ||
    step?.display_name ||
    `${t("Step")} ${index + 1}`;

  const getPhaseLabel = (index) => {
    if (!Array.isArray(phaseLabels)) return "";
    return String(phaseLabels[index] || "").trim();
  };

  return (
    <View
      style={{
        flex: 1,
        minHeight: 0,
        overflow: "hidden",
        borderWidth: 1,
        borderColor: colors.border,
        borderRadius: 16,
        backgroundColor: colors.background,
      }}
    >
      {/* Journey title/header */}
      <View
        style={{
          paddingHorizontal: 16,
          paddingVertical: 16,
          borderBottomWidth: 1,
          borderBottomColor: colors.border,
          backgroundColor: colors.headerBackground,
        }}
      >
        <Text
          style={{
            color: colors.muted,
            fontSize: 12,
            fontWeight: "800",
            textTransform: "uppercase",
            letterSpacing: 0.7,
          }}
        >
          {t("Journey Map")}
        </Text>

        <Text
          style={{
            color: colors.text,
            fontSize: 18,
            lineHeight: 23,
            fontWeight: "900",
            marginTop: 5,
          }}
        >
          {journeyTitle}
        </Text>

        {steps.length > 0 && (
          <Text
            style={{
              color: colors.muted,
              fontSize: 12,
              marginTop: 6,
            }}
          >
            {steps.length}{" "}
            {steps.length === 1 ? t("step") : t("steps")}
          </Text>
        )}
      </View>

      {/* Journey steps */}
      <ScrollView
        style={{
          flex: 1,
        }}
        contentContainerStyle={{
          paddingHorizontal: 12,
          paddingVertical: 14,
          paddingBottom: 28,
        }}
        showsVerticalScrollIndicator
      >
        {steps.map((step, index) => {
          const isSelected = index === activeStepIndex;
          const phaseLabel = getPhaseLabel(index);

        const wasOpened =
        !isSelected && index <= visitedThroughIndex;

        const indicatorColor = isSelected
        ? colors.accent
        : wasOpened
            ? colors.accent
            : colors.muted;

          return (
            <React.Fragment
              key={step?.step_id || step?.id || `journey-step-${index}`}
            >
              <TouchableOpacity
                disabled={restrictToVisited && index > Math.max(activeStepIndex, visitedThroughIndex)}
                onPress={() => {
                  if (restrictToVisited && index > Math.max(activeStepIndex, visitedThroughIndex)) return;
                  onStepChange?.(index);
                }}
                activeOpacity={0.75}
                accessibilityRole="button"
                accessibilityState={{
                  selected: isSelected,
                }}
                accessibilityLabel={`${t("Open step")} ${
                  index + 1
                }: ${phaseLabel ? `${phaseLabel}. ` : ""}${getStepTitle(step, index)}`}
                style={{
                  flexDirection: "row",
                  alignItems: "flex-start",
                  paddingHorizontal: 10,
                  paddingVertical: 11,
                  borderRadius: 12,
                  borderWidth: 1,
                  borderColor: isSelected
                    ? colors.accent
                    : "transparent",
                  backgroundColor: isSelected
                    ? colors.accentSoft
                    : wasOpened
                        ? colors.headerBackground
                        : "transparent",
                }}
              >
                {/* Number/check indicator */}
                <View
                  style={{
                    width: 34,
                    alignItems: "center",
                    flexShrink: 0,
                  }}
                >
                  <View
                    style={{
                      width: 30,
                      height: 30,
                      borderRadius: 15,
                      alignItems: "center",
                      justifyContent: "center",
                      borderWidth: 2,
                      borderColor: indicatorColor,
                      backgroundColor: isSelected
                        ? colors.accent
                        : colors.background,
                    }}
                  >
                    <Text
                      style={{
                        color: isSelected
                          ? "#ffffff"
                          : indicatorColor,
                        fontSize: wasOpened ? 15 : 13,
                        fontWeight: "900",
                      }}
                    >
                      {index + 1}

                    </Text>
                  </View>
                </View>

                {/* Step label */}
                <View
                  style={{
                    flex: 1,
                    minWidth: 0,
                    paddingLeft: 9,
                    paddingTop: 3,
                  }}
                >
                  {!!phaseLabel && (
                    <Text
                      style={{
                        color: isSelected ? colors.accent : colors.muted,
                        fontSize: 10,
                        lineHeight: 13,
                        fontWeight: "900",
                        letterSpacing: 0.8,
                        textTransform: "uppercase",
                        marginBottom: 2,
                      }}
                    >
                      {t(phaseLabel)}
                    </Text>
                  )}

                  <Text
                    style={{
                      color: isSelected
                        ? colors.accent
                        : colors.text,
                      fontSize: 14,
                      lineHeight: 19,
                      fontWeight: isSelected ? "900" : "700",
                    }}
                  >
                    {getStepTitle(step, index)}
                  </Text>

                  {isSelected && (
                    <Text
                      style={{
                        color: colors.muted,
                        fontSize: 11,
                        fontWeight: "700",
                        marginTop: 3,
                      }}
                    >
                      {t("Current step")}
                    </Text>
                  )}
                </View>
              </TouchableOpacity>

              {/* Vertical connector between steps */}
              {index < steps.length - 1 && (
                <View
                  style={{
                    width: 2,
                    height: 12,
                    marginLeft: 26,
                    backgroundColor:
                      index < visitedThroughIndex
                        ? colors.accent
                        : colors.border,
                  }}
                />
              )}
            </React.Fragment>
          );
        })}
      </ScrollView>
    </View>
  );
}